import { Modifier, ModifierGroupInterface, ProductInterface } from '../../services/exports/Interfaces';
import ModifierItem from './Modifier';
import { useTranslation } from 'react-i18next';
import collect from 'collect.js';
import BasketHelpers from '../../services/helpers/BasketHelpers';
import React, { forwardRef, useCallback, useMemo } from 'react';
import { BasketLineItemModifier } from '../../store/types';
import classNames from 'classnames';
import classnames from 'classnames';
import { CompiledModifier } from '../../providers/MenuProvider';

interface Props {
  data: ModifierGroupInterface<CompiledModifier>;
  selectedModifiers: BasketLineItemModifier[];
  toggle: (entry: ProductInterface | Modifier) => void;
  openSubItem: (entry: ProductInterface | Modifier) => void;
  adjustQuantity: (entry: ProductInterface | Modifier, step: number) => void;
  isUpsell?: boolean;
}

const ModifierGroup = forwardRef((props: Props, ref) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Menu:ModifierGroup' });
  const { data, selectedModifiers, toggle, openSubItem, adjustQuantity, isUpsell } = props;
  const isSelected = useCallback(
    (modifier: Modifier | ProductInterface): boolean =>
      !!collect(selectedModifiers).first(
        (item) =>
          item.modifier_group_id === data.id &&
          item.modifier_id === modifier.id &&
          item.modifier_type === modifier.type,
      ),
    [data.id, selectedModifiers],
  );
  const getQuantity = (modifier: Modifier | ProductInterface): number =>
    collect(selectedModifiers).first(
      (item) =>
        item.modifier_group_id === data.id && item.modifier_id === modifier.id && item.modifier_type === modifier.type,
    )?.quantity ?? 0;

  const isRequired = useMemo(() => BasketHelpers.isModifierGroupRequired(data), [data]);

  const hasSelectedModifierGroup = useMemo(
    () =>
      collect(selectedModifiers).contains(
        (item) => item.modifier_group_id === data.id && item.modifier_type === 'modifier',
      ),
    [selectedModifiers, data.id],
  );

  function renderTitle() {
    const title = data.is_variant ? t('select_size') : data.name;

    const getMandatoryLabel = (): string => {
      if (isUpsell) {
        return t('required');
      }

      return isRequired ? t('required') : t('optional');
    };

    return (
      <div
        className={classNames('px-md py-sm bg-gray-500 dark:bg-brand-card', { 'bg-brand-primary/30 py-xs': isUpsell })}
      >
        <div className="flex flex-col space-y-[1px]">
          <h6 className="mr-sm">{title}</h6>
          <div className="flex flex-row items-center gap-x-1 text-brand-text-secondary text-xs">
            <span
              className={classnames({
                'text-[#BF9F68]': !hasSelectedModifierGroup && isRequired,
                'text-primary': hasSelectedModifierGroup && isRequired,
              })}
            >
              {getMandatoryLabel()}
            </span>
            {!isUpsell && data.max_count > 1 && (
              <>
                <span className="mx-1">•</span>
                <span>{t('select_up_to', { maxCount: data.max_count })}</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  const toggleModifier = (entry: Modifier | ProductInterface): void => {
    const isSubItem = !!entry.modifier_group_ids?.length;

    if (!isSubItem) {
      toggle(entry);
      return;
    }

    openSubItem(entry);
  };

  const getNestedModifiers = (modifier: ProductInterface | Modifier): BasketLineItemModifier[] =>
    collect(selectedModifiers).first(
      (item) =>
        item.modifier_group_id === data.id && item.modifier_id === modifier.id && item.modifier_type === modifier.type,
    )?.modifiers;

  const isIncreasable = useMemo(
    (): boolean =>
      !data.max_count || (collect(selectedModifiers).sum((item) => item.quantity) as number) < data.max_count,
    [selectedModifiers, data.max_count],
  );

  return (
    <div ref={ref as React.LegacyRef<HTMLDivElement>}>
      {renderTitle()}
      <div className={classNames('px-md py-sm [&>*]:pb-2xs')}>
        {data.modifiers.map((modifier) => (
          <ModifierItem
            key={`modifier-group-${data.id}-${modifier.type}-${modifier.id}`}
            data={modifier}
            quantity={getQuantity(modifier)}
            isSelected={isSelected(modifier)}
            isSingleSelect={data.max_count === 1}
            hasMultiply={data.multiply}
            toggle={toggleModifier}
            adjustQuantity={(step) => adjustQuantity(modifier, step)}
            selectedModifiers={getNestedModifiers(modifier)}
            isIncreasable={isIncreasable}
            isUpsell={isUpsell}
          />
        ))}
      </div>
    </div>
  );
});

export default ModifierGroup;
